import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { VIRTUAL_IP_FLOW_STEPS } from './virtualIpFlow'
import type { VirtualIpWorkflowProgress } from './virtualIpProgress'
import {
  isVirtualIpStepCompleted,
  isVirtualIpStepStale,
  isVirtualIpStepUnlocked,
  virtualIpStepRouteSegment,
  virtualIpStepStateReason,
} from './virtualIpProgress'

export type VirtualIpWorkflowStepperProps = {
  currentStep: number
  progress: VirtualIpWorkflowProgress
  navigable?: boolean
}

/** Canonical STEP1–STEP7 workflow stepper — center content is always the step number. */
export default function VirtualIpWorkflowStepper({
  currentStep,
  progress,
  navigable = true,
}: VirtualIpWorkflowStepperProps) {
  const { t } = useTranslation('virtualIp')
  const navigate = useNavigate()
  const { workspaceId = '', projectId = '' } = useParams()

  return (
    <nav className="tc-vip-stepper tc-vip-stepper-unified" aria-label={t('flow.ariaLabel')} data-testid="virtual-ip-workflow-stepper">
      <div className="tc-vip-stepper-track">
        {VIRTUAL_IP_FLOW_STEPS.map((item, idx) => {
          const completed = isVirtualIpStepCompleted(progress, item.id)
          const stale = isVirtualIpStepStale(progress, item.id)
          const staleReasonKey = stale ? virtualIpStepStateReason(progress, item.id) : null
          const unlocked = isVirtualIpStepUnlocked(progress, item.id)
          const isCurrent = item.id === currentStep
          const isDone = completed && !isCurrent
          const route = virtualIpStepRouteSegment(item.id)
          const isLocked = !unlocked
          const canNavigate = navigable && !isLocked && !isCurrent && route != null && Boolean(workspaceId)
          const stepLabel = t(item.labelKey)

          const stepState = stale
            ? 'needs-regeneration'
            : isCurrent
              ? 'current'
              : isDone
                ? 'completed'
                : isLocked
                  ? 'locked'
                  : 'available'

          const dot = (
            <button
              type="button"
              className={`tc-vip-stepper-item${isCurrent ? ' is-active' : ''}${isDone ? ' is-done' : ''}${stale ? ' is-stale' : ''}${isLocked ? ' is-locked' : ''}${canNavigate ? ' is-clickable' : ''}`}
              data-testid={`virtual-ip-step-${item.id}-${stepState}`}
              aria-current={isCurrent ? 'step' : undefined}
              aria-disabled={isLocked || isCurrent || !navigable}
              disabled={!canNavigate}
              onClick={() => {
                if (!canNavigate || !route) return
                navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/${route}`)
              }}
            >
              <span className="tc-vip-stepper-dot">{item.id}</span>
              <span className="tc-vip-stepper-label">{t(item.shortLabelKey)}</span>
            </button>
          )

          const tip = isLocked
            ? t('stepper.locked')
            : stale
              ? staleReasonKey
                ? t(staleReasonKey)
                : t('stepper.needsRegeneration', { label: stepLabel })
              : isDone
                ? t('stepper.returnTo', { label: stepLabel })
                : isCurrent
                  ? t('stepper.current', { label: stepLabel })
                  : stepLabel

          return (
            <div key={item.id} className="tc-vip-stepper-segment">
              <Tooltip title={tip}>{dot}</Tooltip>
              {idx < VIRTUAL_IP_FLOW_STEPS.length - 1 ? (
                <span
                  className={`tc-vip-stepper-connector${item.id < progress.furthest_reached_step ? ' is-done' : ''}`}
                />
              ) : null}
            </div>
          )
        })}
      </div>
    </nav>
  )
}
